import { ErrorMessage, Formik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Col,
  Form,
  Input,
  Row,
  Card,
  CardBody,
  Label,
} from "reactstrap";
import TextError from "../../../Components/FormikError/TextError";
import CustomButton from "../../../Components/CustomElements/FormElements/CustomButton";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { checkMemberByPen } from "../../../Api/AdminApis";
import { useState } from "react";
import Constants from "../../../Utils/Constants/Constants";

const PaymentWithoutLogin = () => {

  const [memberDetails, setMemberDetails] = useState<any>(null);
  const [showMemberDetails, setShowMemberDetails] = useState(false);
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    pen_no: Yup.string()
      .required("PEN Number is required")
      .min(1, "PEN Number is required"),
  });

  const initialValues = {
    pen_no: "",
  };

  const handleSubmit = async (values: any, actions: any) => {
    try {
      const response = await checkMemberByPen(values.pen_no);
      
      if (!response?.exists) {
        toast.error(response?.message || "Member not found with this PEN number");
        setShowMemberDetails(false);
        setMemberDetails(null);
        actions.setSubmitting(false);
        return;
      }      
      
      const renewalStatus = response?.renewal_status;

      if (renewalStatus === 6) {
        setMemberDetails(response?.member_details);
        setShowMemberDetails(true);
        toast.success("Membership already renewed");
      }

      // Check if status is 5 (RENEWAL_APPROVED)
      if (renewalStatus === 5) {
        if (response?.payment_expired) {
          setShowMemberDetails(false);
          setMemberDetails(null);
          toast.error(
            response?.message || "Payment link has expired. Please contact admin to resend the payment link.",
            {
              autoClose: 7000,
            }
          );
          actions.setSubmitting(false);
          return;
        }

        if (response?.order_expired) {
          setShowMemberDetails(false);
          setMemberDetails(null);
          toast.error(
            response?.message || "Membership order validity has expired. Please contact admin.",
            {
              autoClose: 7000,
            }
          );
          actions.setSubmitting(false);
          return;
        }
        if (response?.token) {
          // Redirect to payment page with token
          navigate(`/membership-checkout/${response.token}`);
        } else {
          toast.error("Payment token not available. Please contact support.");
        }
      } 
      else if (renewalStatus === Constants.RENEWAL_STATUS.PAYMENT_LINK_EXPIRED) {
        setShowMemberDetails(false);
        setMemberDetails(null);
        toast.error(
          "Payment link has expired. Please contact admin to resend the payment link.",
          {
            autoClose: 7000,
          }
        );
      }
      // Check if status is 1 (PENDING) or 2 (BLOCK_VERIFY)
      else if (renewalStatus === Constants.RENEWAL_STATUS.PENDING) {
        setShowMemberDetails(false);
        setMemberDetails(null);
        toast.warning(
          "Pending for approval. Please contact AOAOK Block approver",
          {
            autoClose: 5000,
          }
        );
      } 
      else if (renewalStatus === Constants.RENEWAL_STATUS.BLOCK_VERIFY) {
        setShowMemberDetails(false);
        setMemberDetails(null);
        toast.warning("Pending for approval. Please contact AOAOK District Office bearers", {
          autoClose: 5000,
        });
      }
      actions.setSubmitting(false);
    } catch (error: any) {
      console.error("Error checking member:", error);
      toast.error(
        error?.response?.data?.message || "An error occurred. Please try again."
      );
      setShowMemberDetails(false);
      setMemberDetails(null);
      actions.setSubmitting(false);
    }
  };

  return (
    <section className="login-wrapper">
      <Container>
        <Row className="align-items-center justify-content-center p-sm-2 p-5">
          <Col md={6}>
            <div className="logo-content-wrap cssanimation sequence fadeInBottom">
              <h2>Welcome to AOAOK</h2>
              <p>Uniting Agriculture for a Sustainable Future</p>
            </div>
          </Col>
          <Col md={4}>
            <div className="login-form-wrap">
              <div className="heading">
                <h3>Renewal Payment Check</h3>
              </div>

{!showMemberDetails ? (
              <Formik
                validationSchema={validationSchema}
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(values: any, actions: any) =>
                  handleSubmit(values, actions)
                }
              >
                {({
                  values,
                  isSubmitting,
                  handleChange,
                  handleSubmit,
                }: any) => {
                  return (
                    <Form onSubmit={handleSubmit}>
                      <div className="form-wrap">
                        <Row className="g-3">
                          <Col lg={12}>
                            <Input
                              type="text"
                              name="pen_no"
                              value={values?.pen_no}
                              onChange={handleChange}
                              placeholder="Enter your PEN no"
                            />
                            <ErrorMessage name="pen_no" component={TextError} />
                          </Col>
                          <Col md={12}>
                            <CustomButton
                              isLoading={isSubmitting}
                              label="Continue"
                              color="primary"
                              className="sm submit-btn w-100"
                            />
                          </Col>
                          <Col md={12}>
                            <div className="text-center">
                              <Link to="/" className="back-link">
                                <i className="icon icon-arrow-left me-2"></i>
                                Back to Login
                              </Link>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
              ) : (
                <div className="member-details-wrap">
                  <div className="mb-3">
                    <div className="alert alert-success mb-3" role="alert">
                      <h5 className="alert-heading mb-2">
                        <i className="icon icon-check-circle me-2"></i>
                        Membership Already Renewed
                      </h5>
                      <p className="mb-0">
                        This member has already renewed their membership profile. 
                        No further payment is required at this time.
                      </p>
                    </div>
                    <Card>
                      <CardBody>
                        <h6 className="mb-3">Member Details</h6>
                        <Row className="g-3">
                          {memberDetails?.first_name && (
                            <Col md={6}>
                              <div className="detail-item">
                                <Label className="detail-label">Name</Label>
                                <p className="detail-value">
                                  {memberDetails.first_name} {memberDetails.last_name}
                                </p>
                              </div>
                            </Col>
                          )}
                          {memberDetails?.membership_no && (
                            <Col md={6}>
                              <div className="detail-item">
                                <Label className="detail-label">Membership Number</Label>
                                <p className="detail-value">{memberDetails.membership_no}</p>
                              </div>
                            </Col>
                          )}
                          {memberDetails?.pen_no && (
                            <Col md={6}>
                              <div className="detail-item">
                                <Label className="detail-label">PEN Number</Label>
                                <p className="detail-value">{memberDetails.pen_no}</p>
                              </div>
                            </Col>
                          )}
                          {memberDetails?.email && (
                            <Col md={6}>
                              <div className="detail-item">
                                <Label className="detail-label">Email</Label>
                                <p className="detail-value">{memberDetails.email}</p>
                              </div>
                            </Col>
                          )}
                          {memberDetails?.district_name && (
                            <Col md={6}>
                              <div className="detail-item">
                                <Label className="detail-label">District</Label>
                                <p className="detail-value">{memberDetails.district_name}</p>
                              </div>
                            </Col>
                          )}
                          {memberDetails?.designation_name && (
                            <Col md={6}>
                              <div className="detail-item">
                                <Label className="detail-label">Designation</Label>
                                <p className="detail-value">{memberDetails.designation_name}</p>
                              </div>
                            </Col>
                          )}
                          {memberDetails?.office_name && (
                            <Col md={6}>
                              <div className="detail-item">
                                <Label className="detail-label">Office</Label>
                                <p className="detail-value">{memberDetails.office_name}</p>
                              </div>
                            </Col>
                          )}
                          {memberDetails?.cadre_name && (
                            <Col md={6}>
                              <div className="detail-item">
                                <Label className="detail-label">Cadre</Label>
                                <p className="detail-value">{memberDetails.cadre_name}</p>
                              </div>
                            </Col>
                          )}
                        </Row>
                      </CardBody>
                    </Card>
                  </div>
                  <Row className="g-3">
                    <Col md={12}>
                      <div className="text-center">
                        <Link to="/" className="back-link">
                          <i className="icon icon-arrow-left me-2"></i>
                          Back to Login
                        </Link>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default PaymentWithoutLogin;
